import { useState, useCallback, useRef } from 'react';
import { subDays } from 'date-fns';
import { DateRange } from 'react-day-picker';
import { DateRangePicker } from './ui/date-range-picker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileLines, faMagnifyingGlass, faTriangleExclamation, faCircleCheck,
  faDownload, faCopy, faRotate, faSpinner, faRobot, faChevronDown,
  faChevronRight, faPlus, faCodeCommit, faGauge,
} from '@fortawesome/free-solid-svg-icons';
import { faJira, faTrello, faConfluence, faNotion, faGoogle } from '@fortawesome/free-brands-svg-icons';
import { ChangelogView } from './ChangelogView';
import {
  generateChangelogFromCommits, detectChangelogGaps,
  calculateHealthScore, enrichWithAI, getEnrichableCommits,
  BATCH_SIZE, AI_MODEL_OPTIONS, TECHNICAL_PROMPT, MARKETING_PROMPT,
  type ChangelogGap, type ChangelogHealthScore,
  type GeneratedChangelog,
} from '../services/changelog-service';
import { fetchIntegrationEvents } from '../services/changelog-integrations';
import { analyzeRepoForChangelog } from '../services/api';
import { getAiSettings, saveAiSettings } from '../services/storage';
import type { Commit, RepoConfig, AiSettings, ChangelogIntegrationConfigs, ChangelogIntegrationEvent, ChangelogIntegrationSource } from '../services/types';

const INTEGRATION_LABELS: Record<ChangelogIntegrationSource, { label: string; icon: React.ReactNode; color: string }> = {
  jira:        { label: 'Jira',         icon: <FontAwesomeIcon icon={faJira} className="text-[#2684FF]" />,                          color: 'bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-500/30' },
  linear:      { label: 'Linear',       icon: <><img src="/brands/linear/dark.svg" className="h-3.5 w-3.5 dark:hidden" alt="" /><img src="/brands/linear/light.svg" className="h-3.5 w-3.5 hidden dark:block" alt="" /></>,    color: 'bg-violet-500/20 text-violet-700 dark:text-violet-300 border-violet-500/30' },
  monday:      { label: 'Monday',       icon: <><img src="/brands/monday/dark.svg" className="h-3.5 w-3.5 dark:hidden" alt="" /><img src="/brands/monday/light.svg" className="h-3.5 w-3.5 hidden dark:block" alt="" /></>,    color: 'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/30' },
  trello:      { label: 'Trello',       icon: <FontAwesomeIcon icon={faTrello} className="text-sky-400" />,                          color: 'bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 border-cyan-500/30' },
  confluence:  { label: 'Confluence',   icon: <FontAwesomeIcon icon={faConfluence} className="text-blue-400" />,                     color: 'bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 border-indigo-500/30' },
  notion:      { label: 'Notion',       icon: <FontAwesomeIcon icon={faNotion} className="text-gray-700 dark:text-gray-200" />,      color: 'bg-slate-500/20 text-slate-700 dark:text-slate-300 border-slate-500/30' },
  'google-docs': { label: 'Google Docs', icon: <FontAwesomeIcon icon={faGoogle} className="text-blue-500" />,                        color: 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' },
};

interface RepoChangelogTabProps {
  commits?: Commit[];
  repoConfig: RepoConfig;
  repoName?: string;
  integrations?: ChangelogIntegrationConfigs;
}

// ── Health Score Badge ────────────────────────────────────────────────────────
function HealthScoreBadge({ score }: { score: ChangelogHealthScore }) {
  const colours: Record<ChangelogHealthScore['grade'], string> = {
    A: 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-500/20 dark:text-emerald-300 dark:border-emerald-500/40',
    B: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-500/20 dark:text-blue-300 dark:border-blue-500/40',
    C: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-500/20 dark:text-amber-300 dark:border-amber-500/40',
    D: 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-500/20 dark:text-orange-300 dark:border-orange-500/40',
    F: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-500/20 dark:text-red-300 dark:border-red-500/40',
  };
  const barColours: Record<ChangelogHealthScore['grade'], string> = {
    A: 'bg-emerald-500', B: 'bg-blue-500', C: 'bg-amber-500', D: 'bg-orange-500', F: 'bg-red-500',
  };
  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${colours[score.grade]}`}>
      <div className="flex items-center gap-2 shrink-0">
        <FontAwesomeIcon icon={faGauge} className="h-4 w-4" />
        <span className="text-2xl font-bold">{score.grade}</span>
        <span className="text-sm font-medium">{score.score}/100</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium truncate">{score.summary}</p>
        <div className="flex gap-2 mt-1.5 items-center">
          <div className="flex-1 h-1.5 rounded-full bg-black/10 dark:bg-white/10 overflow-hidden">
            <div className={`h-full rounded-full ${barColours[score.grade]}`} style={{ width: `${score.score}%` }} />
          </div>
          <span className="text-[10px] shrink-0 opacity-70">
            {Math.round(score.coverage * 100)}% coverage · {Math.round(score.recency * 100)}% recency
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Gap List Item ─────────────────────────────────────────────────────────────
function GapItem({ gap, onCopy }: { gap: ChangelogGap; onCopy: (e: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border border-amber-200 dark:border-amber-500/30 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-start gap-3 px-3 py-2.5 text-left hover:bg-amber-50 dark:hover:bg-amber-500/5 transition-colors"
      >
        <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} className="h-3 w-3 mt-0.5 text-amber-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-mono text-slate-600 dark:text-slate-300 truncate">
            <span className="text-amber-600 dark:text-amber-400 font-semibold mr-1">[{gap.commit.hash.substring(0, 7)}]</span>
            {gap.commit.message}
          </p>
          <p className="text-[10px] text-slate-400 mt-0.5">
            {gap.commit.author} · {new Date(gap.commit.date).toLocaleDateString('en-AU')} · suggest:{' '}
            <span className="font-medium text-blue-500">{gap.suggestedSection}</span>
          </p>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-amber-200 dark:border-amber-500/20 bg-amber-50/50 dark:bg-amber-500/5 px-3 py-2">
          <p className="text-[10px] text-slate-500 dark:text-slate-400 mb-1.5">Suggested entry:</p>
          <code className="text-xs text-slate-700 dark:text-slate-300 block font-mono bg-white dark:bg-slate-800 rounded px-2 py-1.5 border border-amber-200 dark:border-amber-500/20">
            {gap.suggestedEntry}
          </code>
          <button onClick={() => onCopy(gap.suggestedEntry)} className="mt-2 flex items-center gap-1.5 text-[10px] text-blue-600 dark:text-blue-400 hover:underline">
            <FontAwesomeIcon icon={faCopy} className="h-3 w-3" />Copy entry
          </button>
        </div>
      )}
    </div>
  );
}

// ── AI Settings Panel ─────────────────────────────────────────────────────────
function AiSettingsPanel({ settings, onChange }: { settings: Partial<AiSettings>; onChange: (s: Partial<AiSettings>) => void }) {
  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 block mb-1">Provider</label>
        <select
          value={settings.provider || 'openai'}
          onChange={e => onChange({ ...settings, provider: e.target.value as AiSettings['provider'], model: undefined })}
          className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200"
        >
          <option value="openai">OpenAI</option>
          <option value="anthropic">Anthropic</option>
          <option value="custom">Custom (OpenAI-compatible)</option>
        </select>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 block mb-1">API Key</label>
        <input
          type="password"
          value={settings.apiKey || ''}
          onChange={e => onChange({ ...settings, apiKey: e.target.value })}
          placeholder={settings.provider === 'anthropic' ? 'sk-ant-...' : 'sk-...'}
          className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 font-mono"
        />
        <p className="text-[10px] text-slate-400 mt-1">Stored locally in your browser — never sent to VeriGit servers.</p>
      </div>
      {settings.provider !== 'custom' && (
        <div>
          <label className="text-xs font-medium text-slate-600 dark:text-slate-400 block mb-1">Model</label>
          <select
            value={settings.model || ''}
            onChange={e => onChange({ ...settings, model: e.target.value })}
            className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200"
          >
            <option value="">Default</option>
            {AI_MODEL_OPTIONS[settings.provider || 'openai'].map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      )}
      {settings.provider === 'custom' && (
        <div>
          <label className="text-xs font-medium text-slate-600 dark:text-slate-400 block mb-1">Endpoint URL</label>
          <input
            type="url"
            value={settings.customEndpoint || ''}
            onChange={e => onChange({ ...settings, customEndpoint: e.target.value })}
            placeholder="https://your-api/v1/chat/completions"
            className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 font-mono"
          />
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export function RepoChangelogTab({ commits: propCommits = [], repoConfig, repoName, integrations }: RepoChangelogTabProps) {
  const [isDark] = useState(() => document.documentElement.classList.contains('dark'));
  const [innerTab, setInnerTab] = useState<'detected' | 'gaps' | 'generate' | 'ai'>('detected');

  // Analysis state
  const [analysing, setAnalysing] = useState(false);
  const [progressLog, setProgressLog] = useState<string[]>([]);
  const [analyseError, setAnalyseError] = useState('');
  const [internalCommits, setInternalCommits] = useState<Commit[]>([]);
  const [changelogContent, setChangelogContent] = useState<string | null>(null);
  const [changelogFilename, setChangelogFilename] = useState<string | null>(null);
  const [analysed, setAnalysed] = useState(false);

  // Use internal commits if we ran analysis, otherwise fall back to prop commits
  const commits = internalCommits.length > 0 ? internalCommits : propCommits;

  const [gaps, setGaps] = useState<ChangelogGap[]>([]);
  const [healthScore, setHealthScore] = useState<ChangelogHealthScore | null>(null);

  const [generated, setGenerated] = useState<GeneratedChangelog | null>(null);
  const [generating, setGenerating] = useState(false);

  const [aiDraft, setAiDraft] = useState<Partial<AiSettings>>({ provider: 'openai' });
  const [aiSettingsSaved, setAiSettingsSaved] = useState(false);
  const [showAiSetup, setShowAiSetup] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiOutput, setAiOutput] = useState('');
  const [aiError, setAiError] = useState('');
  const [aiBatchProgress, setAiBatchProgress] = useState('');
  const [aiMode, setAiMode] = useState<'technical' | 'marketing'>('technical');
  // Throttle streaming: buffer chunks and flush every 100ms so React isn't hammered
  const streamBufRef = useRef('');
  const streamTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Integration events
  const [enabledIntegrations, setEnabledIntegrations] = useState<Set<ChangelogIntegrationSource>>(new Set());
  const [integrationEvents, setIntegrationEvents] = useState<ChangelogIntegrationEvent[]>([]);
  const [integrationLoading, setIntegrationLoading] = useState(false);
  const [integrationStatus, setIntegrationStatus] = useState<string[]>([]);

  const [copied, setCopied] = useState(false);

  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: subDays(new Date(), 365),
    to: new Date(),
  });

  useState(() => {
    getAiSettings().then(s => {
      if (s) { setAiDraft(s); setAiSettingsSaved(true); }
    });
  });

  const handleAnalyse = useCallback(async () => {
    setAnalysing(true);
    setAnalyseError('');
    setProgressLog([]);
    setChangelogContent(null);
    setChangelogFilename(null);
    setGaps([]);
    setHealthScore(null);
    setGenerated(null);
    setAiOutput('');
    try {
      const result = await analyzeRepoForChangelog(
        repoConfig,
        msg => setProgressLog(prev => [...prev.slice(-29), msg]),
        dateRange?.from?.toISOString(),
        dateRange?.to?.toISOString(),
      );
      setInternalCommits(result.commits);
      setChangelogContent(result.changelogContent);
      setChangelogFilename(result.changelogFilename);
      if (result.changelogContent) {
        const gapList = detectChangelogGaps(result.changelogContent, result.commits);
        setGaps(gapList);
        setHealthScore(calculateHealthScore(result.changelogContent, result.commits, gapList));
      } else {
        setHealthScore(calculateHealthScore('', result.commits, []));
      }
      setAnalysed(true);
    } catch (err) {
      setAnalyseError(err instanceof Error ? err.message : String(err));
    } finally {
      setAnalysing(false);
    }
  }, [repoConfig]);

  const handleGenerate = useCallback(() => {
    setGenerating(true);
    setTimeout(() => {
      setGenerated(generateChangelogFromCommits(commits, repoName));
      setGenerating(false);
    }, 50);
  }, [commits, repoName]);

  const handleSaveAiSettings = useCallback(async () => {
    if (!aiDraft.provider || !aiDraft.apiKey) return;
    await saveAiSettings({
      provider: aiDraft.provider,
      apiKey: aiDraft.apiKey,
      model: aiDraft.model,
      customEndpoint: aiDraft.customEndpoint,
    });
    setAiSettingsSaved(true);
    setShowAiSetup(false);
  }, [aiDraft]);

  const fetchIntegrations = useCallback(async () => {
    if (!integrations || enabledIntegrations.size === 0) return;
    setIntegrationLoading(true);
    setIntegrationStatus([]);
    try {
      // Use commit date range so events align with existing batch windows
      const nonMerge = commits.filter(c => !c.isMerge);
      const commitDates = nonMerge.map(c => new Date(c.date)).sort((a, b) => a.getTime() - b.getTime());
      const integFrom = commitDates.length > 0 ? commitDates[0] : (dateRange?.from ?? new Date(Date.now() - 365 * 86_400_000));
      const integTo   = commitDates.length > 0 ? commitDates[commitDates.length - 1] : (dateRange?.to ?? new Date());
      const events = await fetchIntegrationEvents({
        configs: integrations,
        enabled: [...enabledIntegrations],
        from: integFrom,
        to: integTo,
        onProgress: (msg) => setIntegrationStatus(prev => [...prev, msg]),
      });
      setIntegrationEvents(events);
    } catch (err) {
      console.error('[changelog] integration fetch failed', err);
    } finally {
      setIntegrationLoading(false);
    }
  }, [integrations, enabledIntegrations, commits, dateRange]);

  const toggleIntegration = useCallback((src: ChangelogIntegrationSource) => {
    setIntegrationEvents([]); // clear cached events when selection changes
    setEnabledIntegrations(prev => {
      const next = new Set(prev);
      if (next.has(src)) next.delete(src); else next.add(src);
      return next;
    });
  }, []);

  const handleAiEnrich = useCallback(async () => {
    if (!aiDraft.apiKey || !aiDraft.provider) { setShowAiSetup(true); return; }
    setAiLoading(true); setAiOutput(''); setAiError(''); setAiBatchProgress('');
    try {
      const settings: AiSettings = {
        id: 'ai-settings',
        provider: aiDraft.provider,
        apiKey: aiDraft.apiKey!,
        model: aiDraft.model,
        customEndpoint: aiDraft.customEndpoint,
        updatedAt: new Date().toISOString(),
      };
      // Fetch integration events if integrations are selected but not yet fetched
      let eventsToUse = integrationEvents;
      if (enabledIntegrations.size > 0 && integrationEvents.length === 0 && integrations) {
        setAiBatchProgress('Fetching integration events…');
        // Use commit date range so events align with batch windows
        const nonMerge = commits.filter(c => !c.isMerge);
        const commitDates = nonMerge.map(c => new Date(c.date)).sort((a, b) => a.getTime() - b.getTime());
        const integFrom = commitDates.length > 0 ? commitDates[0] : (dateRange?.from ?? new Date(Date.now() - 365 * 86_400_000));
        const integTo   = commitDates.length > 0 ? commitDates[commitDates.length - 1] : (dateRange?.to ?? new Date());
        eventsToUse = await fetchIntegrationEvents({
          configs: integrations,
          enabled: [...enabledIntegrations],
          from: integFrom,
          to: integTo,
          onProgress: (msg) => setIntegrationStatus(prev => [...prev, msg]),
        }).catch(() => []);
        setIntegrationEvents(eventsToUse);
      }

      // Reset throttle buffer before starting a new enrichment run
      streamBufRef.current = '';
      if (streamTimerRef.current !== null) { clearTimeout(streamTimerRef.current); streamTimerRef.current = null; }

      const enrichResult = await enrichWithAI(commits, settings, chunk => {
        const batchMatch = chunk.match(/<!--\s*Batch (\d+) of (\d+)/);
        if (batchMatch) {
          setAiBatchProgress(`Processing batch ${batchMatch[1]} of ${batchMatch[2]}…`);
        }
        // Buffer chunks; flush to state at most every 100ms to prevent React update depth errors
        streamBufRef.current += chunk;
        if (streamTimerRef.current === null) {
          streamTimerRef.current = setTimeout(() => {
            streamTimerRef.current = null;
            const flushed = streamBufRef.current;
            streamBufRef.current = '';
            setAiOutput(prev => prev + flushed);
          }, 100);
        }
      }, aiMode === 'marketing' ? MARKETING_PROMPT : TECHNICAL_PROMPT, eventsToUse.length > 0 ? eventsToUse : undefined);

      // Flush any remaining buffered content
      if (streamTimerRef.current !== null) { clearTimeout(streamTimerRef.current); streamTimerRef.current = null; }
      if (streamBufRef.current) { setAiOutput(prev => prev + streamBufRef.current); streamBufRef.current = ''; }

      setAiBatchProgress('');
      // Replace streamed output with the final assembled markdown (newest→oldest)
      setAiOutput(enrichResult.markdown);
    } catch (err) {
      setAiError(err instanceof Error ? err.message : String(err));
    } finally {
      setAiLoading(false);
    }
  }, [aiDraft, commits, aiMode, integrationEvents, enabledIntegrations, integrations, dateRange]);

  const copyText = useCallback((text: string) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  }, []);

  const downloadMd = useCallback((content: string, filename: string) => {
    const url = URL.createObjectURL(new Blob([content], { type: 'text/markdown' }));
    const a = Object.assign(document.createElement('a'), { href: url, download: filename });
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  // ── Style helpers ──────────────────────────────────────────────────────────
  const cardBg  = isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200';
  const muted   = isDark ? 'text-slate-400' : 'text-slate-500';
  const tabBase = 'px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5';
  const active  = isDark ? 'bg-slate-700 text-white' : 'bg-white text-slate-800 shadow-sm';
  const inactive = isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700';
  const emptyBox = `rounded-lg border-2 border-dashed ${isDark ? 'border-slate-700' : 'border-gray-200'} p-8 text-center`;

  return (
    <div className="space-y-4 p-1">
      {healthScore && <HealthScoreBadge score={healthScore} />}

      {/* Date range + Tab Bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className={`flex gap-1 p-1 rounded-lg flex-1 ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
          {([
            { key: 'detected' as const, icon: faFileLines,           label: 'Changelog' },
            { key: 'gaps'     as const, icon: faTriangleExclamation, label: `Gaps${gaps.length > 0 ? ` (${gaps.length})` : ''}` },
            { key: 'generate' as const, icon: faCodeCommit,          label: 'Generate' },
            { key: 'ai'       as const, icon: faRobot,               label: 'AI Enrich' },
          ]).map(({ key, icon, label }) => (
            <button key={key} onClick={() => setInnerTab(key)} className={`${tabBase} ${innerTab === key ? active : inactive}`}>
              <FontAwesomeIcon icon={icon} className="h-3 w-3" />{label}
            </button>
          ))}
        </div>
        <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} className="text-xs shrink-0" />
      </div>

      {/* ── Detected Changelog ───────────────────────────────────────── */}
      {innerTab === 'detected' && (
        <div>
          {/* Not yet analysed */}
          {!analysed && !analysing && (
            <div className={emptyBox}>
              <FontAwesomeIcon icon={faFileLines} className={`h-8 w-8 mb-3 mx-auto block ${muted}`} />
              <p className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Clone & detect changelog</p>
              <p className={`text-xs mt-1 mb-4 ${muted}`}>Clones the repository into the browser WASM filesystem, reads the changelog file, and fetches the full commit history.</p>
              {analyseError && (
                <p className="text-xs text-red-500 mb-3 font-mono">{analyseError}</p>
              )}
              <button onClick={handleAnalyse}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md bg-teal-600 text-white hover:bg-teal-700 transition-colors">
                <FontAwesomeIcon icon={faCodeCommit} className="h-3.5 w-3.5" />Analyse Repository
              </button>
            </div>
          )}
          {/* Analysing — show progress */}
          {analysing && (
            <div className={`${emptyBox} text-left`}>
              <div className={`flex items-center gap-2 mb-3 ${muted}`}>
                <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
                <span className="text-sm font-medium">Cloning & analysing…</span>
              </div>
              <div className={`rounded-md font-mono text-[10px] ${isDark ? 'bg-slate-900 text-slate-400' : 'bg-gray-50 text-slate-500'} p-3 max-h-40 overflow-y-auto space-y-0.5`}>
                {progressLog.map((line, i) => <div key={i}>{line}</div>)}
              </div>
            </div>
          )}
          {/* No changelog found after analysis */}
          {analysed && !analysing && !changelogContent && (
            <div className={emptyBox}>
              <FontAwesomeIcon icon={faFileLines} className={`h-8 w-8 mb-3 mx-auto block ${muted}`} />
              <p className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>No changelog file found</p>
              <p className={`text-xs mt-1 ${muted}`}>Checked CHANGELOG.md, CHANGELOG.txt, HISTORY.md, RELEASES.md, CHANGES.md, NEWS.md.</p>
              <div className="flex items-center justify-center gap-2 mt-4">
                <button onClick={() => setInnerTab('generate')} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors">
                  <FontAwesomeIcon icon={faPlus} className="h-3 w-3" />Generate one from {commits.length} commits
                </button>
                <button onClick={handleAnalyse} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border ${isDark ? 'border-slate-600 text-slate-300' : 'border-gray-300 text-slate-600'} hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors`}>
                  <FontAwesomeIcon icon={faRotate} className="h-3 w-3" />Re-analyse
                </button>
              </div>
            </div>
          )}
          {/* Changelog found */}
          {analysed && !analysing && changelogContent && (
            <div className={`rounded-lg border ${cardBg} overflow-hidden`}>
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-slate-700">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faCircleCheck} className="h-3.5 w-3.5 text-emerald-500" />
                  <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{changelogFilename} detected</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isDark ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>{commits.length} commits</span>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => copyText(changelogContent)} className={`text-[10px] flex items-center gap-1 ${muted} hover:text-blue-500 transition-colors`}>
                    <FontAwesomeIcon icon={copied ? faCircleCheck : faCopy} className="h-3 w-3" />{copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button onClick={() => downloadMd(changelogContent, changelogFilename || 'CHANGELOG.md')} className={`text-[10px] flex items-center gap-1 ${muted} hover:text-blue-500 transition-colors`}>
                    <FontAwesomeIcon icon={faDownload} className="h-3 w-3" />Download
                  </button>
                  <button onClick={handleAnalyse} className={`text-[10px] flex items-center gap-1 ${muted} hover:text-blue-500 transition-colors`}>
                    <FontAwesomeIcon icon={faRotate} className="h-3 w-3" />Re-analyse
                  </button>
                </div>
              </div>
              <ChangelogView content={changelogContent} onClose={() => {}} inline />
            </div>
          )}
        </div>
      )}

      {/* ── Gap Analysis ─────────────────────────────────────────────── */}
      {innerTab === 'gaps' && (
        <div className="space-y-3">
          {(!analysed || !changelogContent) ? (
            <div className={emptyBox}>
              <FontAwesomeIcon icon={faMagnifyingGlass} className={`h-8 w-8 mb-3 mx-auto block ${muted}`} />
              <p className={`text-sm ${muted}`}>Run an analysis first to detect the changelog before checking for gaps.</p>
            </div>
          ) : (
            <>
              <div className={`flex items-center justify-between px-4 py-2.5 rounded-lg ${cardBg} border`}>
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faMagnifyingGlass} className="h-3.5 w-3.5 text-amber-500" />
                  <span className="text-sm font-medium">
                    {gaps.length === 0 ? 'No gaps found' : `${gaps.length} commit${gaps.length !== 1 ? 's' : ''} not referenced in changelog`}
                  </span>
                </div>
                {gaps.length > 0 && (
                  <button onClick={() => copyText(gaps.map(g => g.suggestedEntry).join('\n'))} className="text-xs flex items-center gap-1.5 px-2.5 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors">
                    <FontAwesomeIcon icon={faCopy} className="h-3 w-3" />Copy all entries
                  </button>
                )}
              </div>
              {gaps.length === 0 ? (
                <div className="flex items-center gap-2 px-4 py-6 justify-center">
                  <FontAwesomeIcon icon={faCircleCheck} className="h-5 w-5 text-emerald-500" />
                  <span className={`text-sm ${muted}`}>Changelog covers all recent commits.</span>
                </div>
              ) : (
                <div className="space-y-2">
                  {gaps.map((gap, i) => <GapItem key={`${gap.commit.hash}-${i}`} gap={gap} onCopy={copyText} />)}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Generate ─────────────────────────────────────────────────── */}
      {innerTab === 'generate' && (
        <div className="space-y-3">
          {!generated ? (
            <div className={emptyBox}>
              <FontAwesomeIcon icon={faCodeCommit} className={`h-8 w-8 mb-3 mx-auto block ${muted}`} />
              <p className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{commits.length > 0 ? `Generate changelog from ${commits.length} commits` : 'Analyse a repository first to generate a changelog'}</p>
              <p className={`text-xs mt-1 ${muted}`}>Groups by week, classifies by conventional commit type, adds author attribution.</p>
              <button onClick={handleGenerate} disabled={generating || commits.length === 0}
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                {generating
                  ? <><FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" />Generating…</>
                  : <><FontAwesomeIcon icon={faCodeCommit} className="h-3.5 w-3.5" />Generate Changelog</>}
              </button>
            </div>
          ) : (
            <div className={`rounded-lg border ${cardBg} overflow-hidden`}>
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-slate-700">
                <span className={`text-xs ${muted}`}>{generated.commitCount} commits · {generated.versionCount} weeks</span>
                <div className="flex items-center gap-3">
                  <button onClick={() => copyText(generated.markdown)} className={`text-[10px] flex items-center gap-1 ${muted} hover:text-blue-500 transition-colors`}>
                    <FontAwesomeIcon icon={copied ? faCircleCheck : faCopy} className="h-3 w-3" />{copied ? 'Copied!' : 'Copy markdown'}
                  </button>
                  <button onClick={() => downloadMd(generated.markdown, 'CHANGELOG.md')} className={`text-[10px] flex items-center gap-1 ${muted} hover:text-blue-500 transition-colors`}>
                    <FontAwesomeIcon icon={faDownload} className="h-3 w-3" />Download
                  </button>
                  <button onClick={() => setGenerated(null)} className={`text-[10px] flex items-center gap-1 ${muted} hover:text-blue-500 transition-colors`}>
                    <FontAwesomeIcon icon={faRotate} className="h-3 w-3" />Regenerate
                  </button>
                </div>
              </div>
              <ChangelogView content={generated.markdown} onClose={() => {}} inline />
            </div>
          )}
        </div>
      )}

      {/* ── AI Enrich ────────────────────────────────────────────────── */}
      {innerTab === 'ai' && (
        <div className="space-y-4">
          <div className={`rounded-lg border ${cardBg} p-4 space-y-3`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faRobot} className="h-4 w-4 text-violet-500" />
                <span className="text-sm font-medium">AI-Powered Enrichment (BYOK)</span>
              </div>
              <button onClick={() => setShowAiSetup(s => !s)} className={`text-xs flex items-center gap-1.5 ${muted} hover:text-violet-500 transition-colors`}>
                <FontAwesomeIcon icon={faChevronDown} className={`h-3 w-3 transition-transform ${showAiSetup ? 'rotate-180' : ''}`} />
                {aiSettingsSaved ? 'Edit settings' : 'Configure'}
              </button>
            </div>

            {!aiSettingsSaved && !showAiSetup && (
              <p className={`text-xs ${muted} rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'} px-3 py-2`}>
                Provide your own AI API key to enrich commits with human-readable summaries, suggested version bumps, and breaking change detection.
              </p>
            )}

            {showAiSetup && (
              <div className="space-y-3 pt-1">
                <AiSettingsPanel settings={aiDraft} onChange={setAiDraft} />
                <button onClick={handleSaveAiSettings} disabled={!aiDraft.apiKey}
                  className="w-full py-1.5 text-xs font-medium rounded bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  Save API Settings
                </button>
              </div>
            )}

            {aiSettingsSaved && !showAiSetup && (
              <p className={`text-xs flex items-center gap-2 ${muted}`}>
                <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3 text-emerald-500" />
                {aiDraft.provider} · {aiDraft.model || 'default model'} · key saved locally
              </p>
            )}

            {/* Integration sources panel */}
            {integrations && Object.keys(integrations).length > 0 && (() => {
              const available = (Object.keys(integrations) as ChangelogIntegrationSource[]).filter(src => {
                const v = integrations[src as keyof typeof integrations];
                return v !== undefined && v !== null && v !== false;
              });
              if (available.length === 0) return null;
              return (
                <div className={`rounded-lg border ${isDark ? 'border-slate-700 bg-slate-800/50' : 'border-gray-200 bg-gray-50'} p-3 space-y-2`}>
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium">Include Integration Events</p>
                    {integrationEvents.length > 0 && (
                      <span className="text-[10px] text-emerald-500 font-medium">{integrationEvents.length} events loaded</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {available.map(src => {
                      const cfg = INTEGRATION_LABELS[src];
                      const active = enabledIntegrations.has(src);
                      return (
                        <button
                          key={src}
                          onClick={() => toggleIntegration(src)}
                          disabled={aiLoading}
                          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium border transition-all ${
                            active
                              ? cfg.color + ' ring-1 ring-current'
                              : isDark ? 'border-slate-600 text-slate-400 hover:text-slate-200' : 'border-gray-200 text-slate-500 hover:text-slate-700'
                          }`}
                        >
                          <span className="h-3.5 w-3.5 flex items-center justify-center flex-shrink-0">{cfg.icon}</span>
                          {cfg.label}
                        </button>
                      );
                    })}
                  </div>
                  {enabledIntegrations.size > 0 && (
                    <div className="flex items-center justify-between pt-0.5">
                      <p className={`text-[10px] ${muted}`}>
                        {integrationEvents.length > 0
                          ? `${integrationEvents.length} events ready to include in AI context`
                          : 'Events will be fetched when you click Enrich'}
                      </p>
                      <button
                        onClick={fetchIntegrations}
                        disabled={integrationLoading || aiLoading}
                        className={`text-[10px] flex items-center gap-1 text-violet-500 hover:text-violet-700 disabled:opacity-50`}
                      >
                        {integrationLoading
                          ? <><FontAwesomeIcon icon={faSpinner} className="h-2.5 w-2.5 animate-spin" />Fetching…</>
                          : <><FontAwesomeIcon icon={faRotate} className="h-2.5 w-2.5" />Pre-fetch now</>}
                      </button>
                    </div>
                  )}
                  {integrationLoading && integrationStatus.length > 0 && (
                    <p className={`text-[10px] ${muted} font-mono`}>{integrationStatus[integrationStatus.length - 1]}</p>
                  )}
                </div>
              );
            })()}

            {/* Mode toggle */}
            <div className={`flex rounded-lg p-0.5 ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
              {([
                { key: 'technical' as const, label: '🔧 Technical Changelog' },
                { key: 'marketing' as const, label: '📣 Marketing / Press' },
              ] as const).map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setAiMode(key)}
                  disabled={aiLoading}
                  className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    aiMode === key
                      ? isDark ? 'bg-violet-600 text-white shadow' : 'bg-white text-violet-700 shadow'
                      : isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {commits.length > 0 && (() => {
              const enrichable = getEnrichableCommits(commits);
              const mergeCount = commits.length - enrichable.length;
              const batchCount = Math.ceil(enrichable.length / BATCH_SIZE);
              return (
                <p className={`text-[11px] ${muted}`}>
                  <span className="font-medium text-violet-500">{enrichable.length}</span> of {commits.length} commits will be documented
                  {mergeCount > 0 && <span className="ml-1">({mergeCount} merge commits excluded) · {batchCount} batch{batchCount !== 1 ? 'es' : ''}</span>}
                </p>
              );
            })()}

            <button onClick={handleAiEnrich} disabled={aiLoading || commits.length === 0}
              className="w-full py-2 text-sm font-medium rounded-lg bg-gradient-to-r from-violet-600 to-purple-600 text-white hover:from-violet-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2">
              {aiLoading
                ? <><FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" />Enriching…</>
                : <><FontAwesomeIcon icon={faRobot} className="h-3.5 w-3.5" />{commits.length > 0 ? `Enrich ${getEnrichableCommits(commits).length} commits with AI` : 'Analyse repository first'}</>}
            </button>
            {aiLoading && aiBatchProgress && (
              <p className={`text-xs text-center ${muted} animate-pulse`}>{aiBatchProgress}</p>
            )}
          </div>

          {aiError && (
            <div className="rounded-lg border border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10 px-4 py-3 flex items-start gap-2">
              <FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
              <p className="text-xs text-red-700 dark:text-red-400 font-mono break-all">{aiError}</p>
            </div>
          )}

          {aiOutput && (
            <div className={`rounded-lg border ${cardBg} overflow-hidden`}>
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-slate-700">
                <span className="text-xs font-medium text-violet-600 dark:text-violet-400">
                  {aiMode === 'marketing' ? '📣 Marketing / Press Release' : '🔧 AI-Enriched Technical Changelog'}
                </span>
                <div className="flex items-center gap-3">
                  <button onClick={() => copyText(aiOutput)} className={`text-[10px] flex items-center gap-1 ${muted} hover:text-blue-500 transition-colors`}>
                    <FontAwesomeIcon icon={copied ? faCircleCheck : faCopy} className="h-3 w-3" />{copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button onClick={() => downloadMd(aiOutput, aiMode === 'marketing' ? 'RELEASE-NOTES-marketing.md' : 'CHANGELOG-ai.md')} className={`text-[10px] flex items-center gap-1 ${muted} hover:text-blue-500 transition-colors`}>
                    <FontAwesomeIcon icon={faDownload} className="h-3 w-3" />Download
                  </button>
                </div>
              </div>
              <ChangelogView content={aiOutput.replace(/<!--[\s\S]*?-->/g, '').trim()} onClose={() => {}} inline />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
